import {
    getOrMakeUnitDefinitionFromMdsSiMarkup,
    getUnitDefinitionFromCfJsonForVariable,
    getUnitMarkupFromCapabilityVariable,
    convertValueUnit,
} from '@metservicedev/cf-units';
import React from 'react';

import Arrow from '../../../../components/Arrow';
import dataPoint from '../../../../utils/dataPoint';
import datetime2binLabel from '../../../../utils/datetime2binLabel';
import { convertDirectionToCardinal } from '../../../../utils/directionUnits';
import roundNumber from '../../../../utils/roundNumber';

const flipValue180 = (sourceUnit, value) => {
    const degrees = convertValueUnit({ from: sourceUnit, to: 'deg', value });
    const normalisedValue = +((((degrees + 180) % 360) + 360) % 360);
    return convertValueUnit({ from: 'deg', to: sourceUnit, value: normalisedValue });
};

const findIndex = (arr, pred, prevIndex = 0) => {
    const length = arr.length || 0;
    for (let i = prevIndex; i < length; i++) {
        if (pred(arr[i])) {
            return i;
        }
    }
    return -1;
};

const getDisplayValue = ({
    time,
    fromIndex,
    reportTZ,
    wholeDataset,
    wholeCapability,
    binning,
    bins,
    dp,
    binFunction,
    unit,
    directionalVisualization,
    vectorNormalization,
    direction,
    capabilities,
}) => {
    // Returns a display value, and also an index that can be used for subsequent
    // calculations to avoid re-filtering data for every single presentation datum
    // Assumes wholeDataset.data is sorted with respect to time
    let displayValue = null;
    if (!wholeDataset || !wholeCapability) {
        return { displayValue, fromIndex };
    }
    const capabilitySiUnit = getUnitMarkupFromCapabilityVariable(wholeCapability);
    const capabilityUnitDefinition = capabilitySiUnit && getOrMakeUnitDefinitionFromMdsSiMarkup(capabilitySiUnit);

    const capabilityVariable = wholeCapability ? Object.keys(wholeCapability.variables)[0] : null;
    if (!isVariableInCfjson(wholeDataset.cfjson, capabilityVariable)) return { displayValue, fromIndex };
    const datasetUnitDefinition = getUnitDefinitionFromCfJsonForVariable(wholeDataset.cfjson, capabilityVariable);
    const datasetUnit = datasetUnitDefinition.unit;

    const vectorInRadians = vectorNormalization === 'direction';
    const sourceUnit = vectorInRadians ? 'rad' : datasetUnit ? datasetUnit : capabilityUnitDefinition.unit;
    const targetUnit = getOrMakeUnitDefinitionFromMdsSiMarkup(unit || datasetUnit || capabilityUnitDefinition.unit).unit;

    const mdsCapability = capabilities.find(({ id }) => id === wholeCapability.id);
    const mdsVariable = mdsCapability?.variables?.[capabilityVariable];
    const mdsDirection = mdsVariable?.attributes?.direction;
    const shouldFlip180 =
        typeof mdsDirection === 'string' && typeof direction === 'string' && directionalVisualization === 'arrow' && mdsDirection !== direction;

    fromIndex = fromIndex || 0;
    let toIndex;

    if (wholeDataset && wholeDataset.data && sourceUnit && targetUnit) {
        const datums = wholeDataset.data.filter((d) => d && d.time);
        if (!binning) {
            // Get the correct datum
            toIndex = findIndex(datums, (datum) => datum.time.isSame(time), fromIndex);
            if (toIndex > -1 && wholeCapability) {
                const value = dataPoint(datums[toIndex], wholeCapability, vectorInRadians);
                if (value === null) {
                    displayValue = '-';
                } else {
                    const flippedValue = shouldFlip180 ? flipValue180(sourceUnit, value) : value;
                    displayValue = roundNumber({ num: convertValueUnit({ from: sourceUnit, to: targetUnit, value: flippedValue }), dp });
                }
                toIndex++;
            }
        } else {
            // Get the correct slice
            fromIndex = findIndex(datums, (datum) => datum.time && datetime2binLabel({ bins, dt: datum.time, defaultTz: reportTZ }).isSame(time), fromIndex);
            if (fromIndex >= 0) {
                toIndex =
                    findIndex(datums, (datum) => datum.time && !datetime2binLabel({ bins, dt: datum.time, defaultTz: reportTZ }).isSame(time), fromIndex + 1) ||
                    undefined;
                if (toIndex < 0) {
                    // Last day of data, so slice right to the end
                    toIndex = datums.length;
                }
                let relevantDatums = datums.slice(fromIndex, toIndex);
                relevantDatums = relevantDatums.map((datum) => dataPoint(datum, wholeCapability, vectorInRadians));
                if (relevantDatums.length > 0 && wholeCapability) {
                    try {
                        const value = binFunction(relevantDatums.filter((relevantDatum) => !(relevantDatum === null || relevantDatum === undefined)));
                        if (value === null) {
                            displayValue = '-';
                        } else {
                            const flippedValue = shouldFlip180 ? flipValue180(sourceUnit, value) : value;
                            displayValue = roundNumber({ num: convertValueUnit({ from: sourceUnit, to: targetUnit, value: flippedValue }), dp });
                        }
                    } catch (e) {
                        displayValue = '-';
                    }
                }
            }
        }
    }
    if (sourceUnit === '' && targetUnit === '' && wholeDataset && wholeDataset.data) {
        const datums = wholeDataset.data.filter((d) => d && d.time);
        toIndex = findIndex(datums, (datum) => datum.time.isSame(time), fromIndex);
        displayValue = dataPoint(datums[toIndex], wholeCapability, vectorInRadians);
        if (displayValue && !isNaN(displayValue)) displayValue = parseFloat(displayValue.toFixed(dp));
    }

    if (displayValue == null || isNaN(displayValue)) {
        displayValue = null; // Do not evaluate 0 to false!
    } else if ((capabilityUnitDefinition && capabilityUnitDefinition.isDirection) || vectorInRadians) {
        const degrees = convertValueUnit({ from: unit, to: 'deg', value: displayValue });

        switch (directionalVisualization) {
            case 'original':
                // Respect selected unit
                break;
            case 'cardinal':
                displayValue = convertDirectionToCardinal({ value: displayValue, unit });
                break;
            case 'arrow':
                displayValue = <Arrow degrees={degrees} />;
                break;
        }
    }
    return { displayValue, fromIndex: toIndex };
};

export default getDisplayValue;

const isVariableInCfjson = (cfjson, variable) => {
    const standardNamedVariable = Object.values(cfjson.variables).find((v) => {
        if (v.attributes.standard_name === variable) return true;
        return false;
    });
    if (standardNamedVariable) return true;
    if (cfjson.variables[variable]) return true;
    return false;
};
